/**
 * Focus handling for components that open over the page (the accessory catalog
 * overlay, its image lightbox, the action-price dialog). Everything below is
 * what Modal.vue already does for the classic modals, extracted so the dialogs
 * that do not go through Modal.vue behave the same:
 *
 * - the element that had focus when the dialog opened gets it back on close
 * - Tab and Shift+Tab cycle inside the dialog instead of walking the page
 * - the body does not scroll behind the dialog
 *
 * Usage:
 *
 *   import modalFocus from '@/mixins/modalFocus'
 *   export default { mixins: [modalFocus] }
 *
 * Optional overrides — define either as a normal method on the component and it
 * wins over the mixin's (component options take precedence):
 *
 *   modalFocusRoot()   -> the element to trap focus inside. Defaults to `$el`,
 *                         which is right for a single-root component; a
 *                         multi-root component must return a real element.
 *   modalFocusFirst()  -> what to focus on open (an element, or a component
 *                         with an `$el`). Defaults to the first focusable
 *                         descendant of the root.
 *
 * Two dialogs can be open at once (the lightbox opens on top of the overlay),
 * so the mixin keeps a module-level stack of mounted instances. Only the top of
 * the stack traps Tab, and the body scroll lock is taken by the first instance
 * and released by the last — one dialog closing never unlocks the page under
 * the one still open.
 */

/** Mounted instances, innermost last. */
const stack = []

/**
 * The body's own inline `overflow` from before the first dialog took the lock.
 * Modal.vue restores this with `removeProperty('overflow')`, which throws away
 * an inline value the page set itself; saving and writing it back is what makes
 * these dialogs safe to nest inside anything else that locks scrolling.
 */
let bodyOverflow = null

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const isVisible = (element) =>
  !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length)

export default {
  data() {
    return {
      // The element focus came from. Not reactive in any useful sense, but data()
      // is where Modal.vue keeps it and it makes the state discoverable.
      modalFocusPrevious: null,
    }
  },
  mounted() {
    this.modalFocusPrevious = document.activeElement

    stack.push(this)
    if (stack.length === 1) {
      bodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }

    // Capture phase: the dialog gets the Tab before anything inside it does.
    document.addEventListener('keydown', this.modalFocusOnKeydown, true)

    this.$nextTick(() => {
      // A template ref can be a component, whose element is on $el.
      const chosen = this.modalFocusFirst()
      const target = (chosen && chosen.$el) || chosen || this.modalFocusables()[0]
      if (target && typeof target.focus === 'function') target.focus()
    })
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.modalFocusOnKeydown, true)

    const index = stack.indexOf(this)
    if (index !== -1) stack.splice(index, 1)
    if (stack.length === 0) {
      // '' clears the inline declaration, which is what an untouched body had.
      document.body.style.overflow = bodyOverflow || ''
      bodyOverflow = null
    }

    // The element may be gone (the dialog was opened from a row that has since
    // been re-rendered), in which case there is nothing to restore to.
    const previous = this.modalFocusPrevious
    if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
      previous.focus()
    }
  },
  methods: {
    /** Override point: the element focus is trapped inside. */
    modalFocusRoot() {
      // $el is a placeholder comment/text node for a multi-root component.
      return this.$el && this.$el.nodeType === Node.ELEMENT_NODE ? this.$el : null
    },
    /** Override point: what to focus on open. Null means "first focusable". */
    modalFocusFirst() {
      return null
    },
    /** Focusable descendants of the root, in tab order, skipping hidden ones. */
    modalFocusables() {
      const root = this.modalFocusRoot()
      if (!root) return []
      return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible)
    },
    modalFocusOnKeydown(event) {
      if (event.key !== 'Tab') return
      // Only the innermost open dialog traps; the ones underneath stay quiet.
      if (stack[stack.length - 1] !== this) return

      const root = this.modalFocusRoot()
      if (!root) return

      const focusables = this.modalFocusables()
      if (!focusables.length) {
        // Nothing to move to, so keep focus where it is rather than let it out.
        event.preventDefault()
        return
      }

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      const outside = !root.contains(active)

      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault()
        first.focus()
      }
    },
  },
}
